import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, FormGroup, Label } from 'reactstrap';
import axios from 'axios';
import Select from 'react-select';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import { H5 } from '../../../AbstractElements';
import config from '../../../config';
import Widgets1 from '../../Common/CommonWidgets/Widgets1';
import licenseService from '../../../Services/licenseService';
import LicenseSummaryCard from './LicenseSummaryCard';
import LicenseGroupCard from './LicenseGroupCard';
import LicenseUsageBanner from './LicenseUsageBanner';
import LicenseTable from './LicenseTable';
import CreatePOForm from './CreatePOForm';
import POListTable from './POListTable';
import ConsumptionLogTable from './ConsumptionLogTable';
import GroupsAdmin from './GroupsAdmin';
import './licenses.scss';

const authHeader = () => {
    const token = localStorage.getItem('sessiontoken');
    return { headers: { Authorization: `${token}` } };
};

const LicensesAdminPage = () => {
    const { t } = useTranslation();
    const concessionaireId = localStorage.getItem('concessionaireId');
    const propertyIdCsv = localStorage.getItem('propertyAccess') || '';
    const accessLicenses = localStorage.getItem('accessLicenses');
    const manageLicenses = localStorage.getItem('manageLicenses');
    const isPermissionGated = accessLicenses !== '1' && manageLicenses !== '1';

    const location = useLocation();
    const canManagePOs = manageLicenses === '1';
    // Initial tab: '2' (Purchase Orders) only if the URL points at the PO sub-path
    // AND the user is allowed to see it. Otherwise default to '1' (Overview).
    // Purely local state after mount — clicking a tab should NEVER be fought by
    // an effect that re-derives tab from the URL.
    const initialTab = canManagePOs && /\/licenses\/po(\/|$)/.test(location.pathname) ? '2' : '1';

    const [assets, setAssets] = useState([]);                 // all properties in the user's scope
    const [groups, setGroups] = useState([]);                 // license groups in this concessionaire
    const [statusByAsset, setStatusByAsset] = useState({});   // asset-wise status
    const [statusByGroup, setStatusByGroup] = useState({});   // group status (pool + memberBreakdown)
    const [selectedAssetId, setSelectedAssetId] = useState('');
    const [tab, setTab] = useState(initialTab);
    const [poRefreshKey, setPoRefreshKey] = useState(0);
    const [groupRefreshKey, setGroupRefreshKey] = useState(0);

    // Load properties
    useEffect(() => {
        if (!concessionaireId) return;
        const trimmed = (propertyIdCsv || '').trim();
        const hasPropertyFilter = trimmed && trimmed !== '[]' && trimmed !== '""';
        axios
            .post(`${config.hostname}/property/getpropertybyconcessionaire`,
                {
                    property_concessionaire_id: concessionaireId,
                    ...(hasPropertyFilter ? { property_id: trimmed } : {})
                },
                authHeader())
            .then((response) => {
                const data = response.data || [];
                setAssets(data);
                if (data.length > 0) setSelectedAssetId(data[0]._id);
            })
            .catch((err) => console.log(err));
    }, [concessionaireId, propertyIdCsv]);

    // Load groups — sort so smaller / more specific groups (e.g. retail
    // clusters like WTC) render before larger generic pools (e.g. community
    // malls). Keeps retail-adjacent cards together on the Overview.
    useEffect(() => {
        licenseService.listGroups()
            .then(res => {
                const sorted = [...(res.data || [])].sort((a, b) => {
                    const la = (a.members || []).length;
                    const lb = (b.members || []).length;
                    if (la !== lb) return la - lb;
                    return String(a.group_name).localeCompare(String(b.group_name));
                });
                setGroups(sorted);
            })
            .catch(err => console.log(err));
    }, [groupRefreshKey]);

    // Per-asset status — ONLY for properties not in a group
    useEffect(() => {
        if (!assets.length) return;
        let cancelled = false;
        const standaloneAssets = assets.filter(a => !a.property_license_group_id);
        Promise.all(
            standaloneAssets.map(a =>
                licenseService.getAssetStatus(a._id)
                    .then(res => ({ id: a._id, status: res.data }))
                    .catch(() => ({ id: a._id, status: null }))
            )
        ).then(results => {
            if (cancelled) return;
            const next = {};
            results.forEach(r => { if (r.status) next[r.id] = r.status; });
            setStatusByAsset(next);
        });
        return () => { cancelled = true; };
    }, [assets]);

    // Per-group status
    useEffect(() => {
        if (!groups.length) return;
        let cancelled = false;
        Promise.all(
            groups.map(g =>
                licenseService.getGroupStatus(g._id)
                    .then(res => ({ id: g._id, status: res.data }))
                    .catch(() => ({ id: g._id, status: null }))
            )
        ).then(results => {
            if (cancelled) return;
            const next = {};
            results.forEach(r => { if (r.status) next[r.id] = r.status; });
            setStatusByGroup(next);
        });
        return () => { cancelled = true; };
    }, [groups, groupRefreshKey]);

    // Assets that belong to a group are rendered INSIDE the group card.
    const standaloneAssets = useMemo(
        () => assets.filter(a => !a.property_license_group_id),
        [assets]
    );

    const warnEntities = useMemo(() => {
        const flagged = [];
        standaloneAssets.forEach(a => {
            const s = statusByAsset[a._id];
            if (s && s.usagePct > 80) flagged.push({ property_mall_code: a.property_mall_code });
        });
        groups.forEach(g => {
            const s = statusByGroup[g._id];
            if (s && s.usagePct > 80) flagged.push({ property_mall_code: g.group_code });
        });
        return flagged;
    }, [standaloneAssets, statusByAsset, groups, statusByGroup]);

    // KPI filter — null means "all", else `prop:<id>` or `group:<id>`.
    const [kpiFilter, setKpiFilter] = useState(null);

    const aggregates = useMemo(() => {
        const agg = { total: 0, available: 0, consumed: 0, overdrafts: 0 };
        // Filtered to a single entity — show just that entity's numbers.
        if (kpiFilter) {
            const [kind, id] = kpiFilter.split(':');
            const s = kind === 'group' ? statusByGroup[id] : statusByAsset[id];
            if (s) {
                agg.total = s.total;
                agg.available = s.available;
                agg.consumed = s.consumed;
                agg.overdrafts = s.overdrafts;
            }
            return agg;
        }
        standaloneAssets.forEach(a => {
            const s = statusByAsset[a._id];
            if (!s) return;
            agg.total += s.total;
            agg.available += s.available;
            agg.consumed += s.consumed;
            agg.overdrafts += s.overdrafts;
        });
        groups.forEach(g => {
            const s = statusByGroup[g._id];
            if (!s) return;
            agg.total += s.total;
            agg.available += s.available;
            agg.consumed += s.consumed;
            agg.overdrafts += s.overdrafts;
        });
        return agg;
    }, [standaloneAssets, statusByAsset, groups, statusByGroup, kpiFilter]);

    // Shared entity options (asset OR group) — used for both KPI filter and Ledger dropdown.
    const entityOptions = useMemo(() => {
        const opts = [];
        standaloneAssets.forEach(p => opts.push({ value: `prop:${p._id}`, label: `${p.property_name} (${p.property_mall_code})` }));
        groups.forEach(g => opts.push({ value: `group:${g._id}`, label: t('[Group] {{name}}', { name: g.group_name }) }));
        return opts;
    }, [groups, standaloneAssets, t]);
    const [ledgerSelection, setLedgerSelection] = useState('');
    useEffect(() => {
        if (!ledgerSelection && entityOptions.length > 0) {
            setLedgerSelection(entityOptions[0].value);
        }
    }, [entityOptions, ledgerSelection]);

    if (isPermissionGated) {
        return (
            <Fragment>
                <Breadcrumbs parent={t('Admin')} title={t('License Inventory')} />
                <Container fluid>
                    <Card>
                        <CardBody>
                            <H5>{t('License Inventory')}</H5>
                            <p className='mt-2 f-light'>
                                {t("You don't have the")} <code>access_licenses</code> {t('or')} <code>manage_licenses</code> {t('permission. Please contact a Backend User to grant access.')}
                            </p>
                        </CardBody>
                    </Card>
                </Container>
            </Fragment>
        );
    }

    const [ledgerKind, ledgerId] = (ledgerSelection || '').split(':');

    return (
        <Fragment>
            <Breadcrumbs parent={t('Admin')} title={t('License Inventory')} />
            <Container fluid className='v3 v3-license-page'>
                <LicenseUsageBanner warnAssets={warnEntities} />

                <Row className='mb-2'>
                    <Col md='6' lg='4'>
                        <FormGroup className='mb-2'>
                            <Label>{t('Filter KPIs by asset / group')}</Label>
                            <Select
                                placeholder={t('All assets and groups')}
                                isClearable
                                value={entityOptions.find(o => o.value === kpiFilter) || null}
                                options={entityOptions}
                                onChange={opt => setKpiFilter(opt?.value || null)}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition='fixed'
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <Row className='mb-2 g-3'>
                    <Col><Widgets1 data={{ title: t('Total Licenses'), total: aggregates.total, color: 'primary', icon: 'customers' }} /></Col>
                    <Col><Widgets1 data={{ title: t('Available'), total: aggregates.available, color: 'success', icon: 'customers' }} /></Col>
                    <Col><Widgets1 data={{ title: t('Consumed'), total: aggregates.consumed, color: 'warning', icon: 'customers' }} /></Col>
                    <Col><Widgets1 data={{ title: t('Overdrafts'), total: aggregates.overdrafts, color: 'secondary', icon: 'customers' }} /></Col>
                </Row>

                <Card>
                    <CardHeader>
                        <H5 attrH5={{ className: 'mb-0' }}>{t('License Inventory & Lifecycle')}</H5>
                    </CardHeader>
                    <CardBody>
                        <Nav className='border-tab' tabs>
                            <NavItem>
                                <NavLink href='#javascript' className={tab === '1' ? 'active' : ''} onClick={(e) => { e.preventDefault(); setTab('1'); }}>{t('Overview')}</NavLink>
                            </NavItem>
                            {canManagePOs && (
                                <NavItem>
                                    <NavLink href='#javascript' className={tab === '2' ? 'active' : ''} onClick={(e) => { e.preventDefault(); setTab('2'); }}>{t('Purchase Orders')}</NavLink>
                                </NavItem>
                            )}
                            <NavItem>
                                <NavLink href='#javascript' className={tab === '3' ? 'active' : ''} onClick={(e) => { e.preventDefault(); setTab('3'); }}>{t('Consumption Log')}</NavLink>
                            </NavItem>
                            {canManagePOs && (
                                <NavItem>
                                    <NavLink href='#javascript' className={tab === '4' ? 'active' : ''} onClick={(e) => { e.preventDefault(); setTab('4'); }}>{t('Groups')}</NavLink>
                                </NavItem>
                            )}
                        </Nav>

                        <TabContent activeTab={tab} className='mt-3'>
                            <TabPane tabId='1'>
                                {tab === '1' && (
                                    <>
                                        <Row className='g-3'>
                                            {standaloneAssets.length === 0 && groups.length === 0 ? (
                                                <Col><Card><CardBody>{t('No assigned assets.')}</CardBody></Card></Col>
                                            ) : (
                                                standaloneAssets.map(a => (
                                                    <LicenseSummaryCard key={a._id} asset={a} status={statusByAsset[a._id]} />
                                                ))
                                            )}
                                            {groups.map(g => (
                                                <LicenseGroupCard key={g._id} group={g} status={statusByGroup[g._id]} />
                                            ))}
                                        </Row>

                                        <Row className='mt-4'>
                                            <Col md='6'>
                                                <FormGroup>
                                                    <Label>{t('Ledger view')}</Label>
                                                    <Select
                                                        placeholder={t('Select property or group')}
                                                        isClearable
                                                        value={entityOptions.find(o => o.value === ledgerSelection) || null}
                                                        options={entityOptions}
                                                        onChange={opt => setLedgerSelection(opt?.value || '')}
                                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                        menuPosition='fixed'
                                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {ledgerKind === 'prop' && ledgerId && (
                                            <LicenseTable assetId={ledgerId} />
                                        )}
                                        {ledgerKind === 'group' && ledgerId && (
                                            <LicenseTable assetId={null} groupId={ledgerId} />
                                        )}
                                    </>
                                )}
                            </TabPane>

                            {canManagePOs && (
                                <TabPane tabId='2'>
                                    {tab === '2' && (
                                        <>
                                            <CreatePOForm onCreated={() => setPoRefreshKey(k => k + 1)} scopedToConcessionaire />
                                            <div className='mt-3'>
                                                <POListTable refreshKey={poRefreshKey} />
                                            </div>
                                        </>
                                    )}
                                </TabPane>
                            )}

                            <TabPane tabId='3'>
                                {tab === '3' && <ConsumptionLogTable />}
                            </TabPane>

                            {canManagePOs && (
                                <TabPane tabId='4'>
                                    {tab === '4' && (
                                        <GroupsAdmin
                                            refreshKey={groupRefreshKey}
                                            onChanged={() => setGroupRefreshKey(k => k + 1)}
                                        />
                                    )}
                                </TabPane>
                            )}
                        </TabContent>
                    </CardBody>
                </Card>
            </Container>
        </Fragment>
    );
};

export default LicensesAdminPage;
